import { produce } from 'immer'
import { useCallback, useRef, useState } from 'react'
import AvatarEditor from 'react-avatar-editor'
import { useTranslation } from 'react-i18next'

import {
    Button, Dialog, DialogBody, DialogProps, InputGroup, Intent, Slider
} from '@blueprintjs/core'
import { IconNames } from '@blueprintjs/icons'

import profile from '../../../domain/api/profile'
import Empty from '../../common/empty/Empty'
import PrimaryButton from '../../common/primaryButton/PrimaryButton'
import { useRootOutletContext } from '../../root/Root'
import styles from './SelectProfileImage.module.scss'

export default ({
    profileId,
    onUpload,
    onRemove,
    canRemove,
    dialogProps,
}: {
    profileId: string
    onUpload?: (dataUrl: string) => void
    onRemove?: () => void
    canRemove?: boolean
    dialogProps?: DialogProps
}) => {
    const imageSize = 256

    const { t } = useTranslation()
    const { toaster } = useRootOutletContext()
    const { trigger: place, isMutating: isPlacing } = profile.image.usePlaceSWR(profileId)
    const { trigger: reject, isMutating: isRejecting } = profile.image.useRejectSWR(profileId)
    const editorRef = useRef<AvatarEditor>(null)
    const fileInputRef = useRef<HTMLInputElement>(null)

    const [state, setState] = useState<{
        file: File | undefined,
        scale: number,
    }>({
        file: undefined,
        scale: 1.2,
    })

    const onFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files
        if (!files) return
        const file = files[0]
        if (!file) return
        setState(produce(draft => {
            draft.file = file
        }))
    }, [])

    const submit = useCallback(async () => {
        if (!editorRef.current) return
        const dataUrl = editorRef.current.getImageScaledToCanvas().toDataURL()
        const result = await place(dataUrl)

        if (result.status !== 204) return toaster?.show({ message: t('toast.failedToUploadProfileImage.message'), icon: IconNames.Error, intent: Intent.DANGER })
        toaster?.show({ message: t('toast.profileImageUploaded.message'), icon: IconNames.Upload, intent: Intent.SUCCESS })

        onUpload?.(dataUrl)
        setState(produce(draft => {
            draft.file = undefined
        }))
    }, [place, toaster, t, onUpload])

    const remove = useCallback(async () => {
        const result = await reject()

        if (result.status !== 204) return toaster?.show({ message: t('toast.failedToRemoveProfileImage.message'), icon: IconNames.Error, intent: Intent.DANGER })
        toaster?.show({ message: t('toast.profileImageRemoved.message'), icon: IconNames.Trash, intent: Intent.SUCCESS })

        onRemove?.()
    }, [reject, toaster, t, onRemove])

    return <>
        <Dialog {...dialogProps}
            className={`${styles.dialog} ${dialogProps?.className}`}
            title={dialogProps?.title ?? t('profileDetail.selectProfileImage.title')}
            isCloseButtonShown={dialogProps?.isCloseButtonShown ?? true}
            canEscapeKeyClose={dialogProps?.canEscapeKeyClose ?? true}
            canOutsideClickClose={dialogProps?.canOutsideClickClose ?? true}
        >
            <DialogBody className={styles.body}>
                {state.file ? <>
                    <AvatarEditor
                        className={styles.editor}
                        ref={editorRef}
                        image={state.file}
                        width={imageSize}
                        height={imageSize}
                        border={25}
                        borderRadius={imageSize / 2}
                        color={[255, 255, 255, 0.5]}
                        scale={state.scale}
                        rotate={0} />
                    <Slider
                        min={1}
                        max={4}
                        value={state.scale}
                        stepSize={0.01}
                        labelRenderer={false}
                        onChange={value => setState(produce(draft => {
                            draft.scale = value
                        }))}

                    />
                    <PrimaryButton
                        text={t('profileDetail.selectProfileImage.submitButton.label')}
                        icon={IconNames.Tick}
                        onClick={submit}
                        disabled={isPlacing} />
                    <PrimaryButton
                        text={t('profileDetail.selectProfileImage.backButton.label')}
                        icon={IconNames.Undo}
                        onClick={() => setState(produce(draft => {
                            draft.file = undefined
                        }))}
                        disabled={isPlacing}
                        alternative />
                </> : <>
                    <Empty
                        lottie="camera"
                        title={t('profileDetail.selectProfileImage.uploadImage.title')}
                        description={t('profileDetail.selectProfileImage.uploadImage.description')}
                        buttonProps={{
                            text: t('profileDetail.selectProfileImage.uploadImage.button.label'),
                            icon: IconNames.Camera,
                            onClick: () => fileInputRef.current?.click()
                        }} />
                    {canRemove && <>
                        <Button
                            className={styles.removeButton}
                            text={t('profileDetail.selectProfileImage.removeButton.label')}
                            icon={IconNames.Trash}
                            intent={Intent.DANGER}
                            onClick={remove}
                            disabled={isRejecting}
                            fill />
                    </>}
                    <InputGroup
                        className={styles.fileInput}
                        inputRef={fileInputRef}
                        type="file"
                        accept="image/*"
                        onChange={onFileChange}
                        fill />
                </>}
            </DialogBody>
        </Dialog>
    </>
}